import { useState } from 'react';
import { Box } from '@mui/material';
import colors from '../../../../assets/theme/base/colors';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import AppAccordion from '../../../../Component/Common/Components/Accordion/AppAccordion';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { EEOccupationalLevel } from '../../../../Component/Model/Enum/EmploymentEquity';
import { occupationalLevelDisplayNames } from '../Shared/deviationsCommon';
import { PlanSummaryTable } from './PlanSummaryTable';
import { PlanSummaryLegend } from './PlanSummaryLegend';
import { OccupationalLevelJustificationsTable } from './OccupationalLevel/OccupationalLevelJustificationsTable';
import { DemographicJustificationsTable } from './Demographic/DemographicJustificationsTable';
import { RecruitmentPromotionMovementsTable } from './IndividualJustifications/RecruitmentPromotionMovementsTable';
import { SeverityCountChips, KeyReasonChip } from './AccordionHeaderChips';
import { SeverityChipCounts, getSeverityCounts } from './justificationsHelper';
import { JustificationSummaryStatCards } from './JustificationSummaryStatCards';

interface OccupationalLevelAccordionProps {
    scorecardId: string;
    occupationalLevel: DeviationsDto.PlanSummaryOccupationalLevelDto;
    periodHeading: string;
    justificationSummary: DeviationsDto.JustificationSummaryItemDto | null;
    onJustificationsChanged: () => void;
    defaultExpanded?: boolean;
}

export function OccupationalLevelAccordion({ scorecardId, occupationalLevel, periodHeading, justificationSummary, onJustificationsChanged, defaultExpanded = false }: OccupationalLevelAccordionProps) {
    const displayName: string = occupationalLevelDisplayNames[occupationalLevel.occupationalLevelId as EEOccupationalLevel] ?? 'Unspecified';
    const [hasExpanded, setHasExpanded] = useState<boolean>(defaultExpanded);
    const severityCounts: SeverityChipCounts = getSeverityCounts(occupationalLevel);

    return (
        <Box sx={{ mb: 1 }}>
            <AppAccordion
                expandOnDefault={defaultExpanded}
                onExpandChange={(expanded) => { if (expanded) setHasExpanded(true); }}
                label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box component="span" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>{displayName}</Box>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '16px' }}>
                            <SeverityCountChips counts={severityCounts} />
                            {justificationSummary && <KeyReasonChip captured={justificationSummary.eea2KeyJustifiableReasonCaptured} />}
                        </Box>
                    </Box>
                }
                height="56px"
            >
                <Box sx={{ fontSize: '1.25rem', fontWeight: 500, color: colors.dark.main, mb: 2 }}>
                    {periodHeading}
                </Box>
                <PlanSummaryTable
                    occupationalLevel={occupationalLevel}
                    scorecardId={scorecardId}
                    occupationalLevelDisplayName={displayName}
                />
                <PlanSummaryLegend />
                {justificationSummary && <JustificationSummaryStatCards summary={justificationSummary} />}
                <MDTypography variant="h6" fontWeight="medium" color="dark" sx={{ mt: 3, mb: 1 }}>
                    Key Justification Reasons (EEA2)
                </MDTypography>
                <Box sx={{ mt: 3 }}>
                    <OccupationalLevelJustificationsTable
                        occupationalLevelType={occupationalLevel.occupationalLevelId}
                        isOpen={hasExpanded}
                        onJustificationsChanged={onJustificationsChanged}
                    />
                </Box>
                <Box sx={{ mt: 3 }}>
                    <DemographicJustificationsTable
                        occupationalLevelType={occupationalLevel.occupationalLevelId}
                        isDisabled={false}
                        isOpen={hasExpanded}
                        onJustificationsChanged={onJustificationsChanged}
                    />
                </Box>
                {hasExpanded && (
                    <RecruitmentPromotionMovementsTable
                        scorecardId={scorecardId}
                        occupationalLevelId={occupationalLevel.occupationalLevelId}
                        occupationalLevelDisplayName={displayName}
                        onJustificationsChanged={onJustificationsChanged}
                    />
                )}
            </AppAccordion>
        </Box>
    );
}
